/**
 * Studio Course Mutation Hooks
 *
 * Symmetric to `studio-series.mutations.ts`. The atomic move mutation lives in
 * the series module (endpoint is video-scoped); re-exported here so course
 * consumers can `import { useMoveVideo } from '@/api/mutations/studio-courses.mutations'`.
 *
 * Same caveats apply (destructure stable methods, don't swallow error codes).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { studioCoursesClient } from '../client/studio-courses.client';
import { queryKeys } from '../query-keys';
import type { CreateStudioCoursePayload, UpdateStudioCoursePayload } from '@/types/studio-courses';
import type {
  BatchAttachVideosPayload,
  BatchAttachVideosResponse,
  ReorderVideosPayload,
  ReorderVideosResponse,
} from '@/types/studio-series';

export { useMoveVideo } from './studio-series.mutations';

const STUDIO_COURSES_PREFIX = ['studio', 'courses'] as const;
const STUDIO_VIDEOS_PREFIX = ['studio', 'videos'] as const;

export function useCreateStudioCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateStudioCoursePayload) => studioCoursesClient.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_COURSES_PREFIX });
    },
  });
}

export function useUpdateStudioCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uuid, payload }: { uuid: string; payload: UpdateStudioCoursePayload }) =>
      studioCoursesClient.update(uuid, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_COURSES_PREFIX });
    },
  });
}

export function useClearStudioCourseThumbnail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioCoursesClient.clearThumbnail(uuid),
    onSuccess: (_data, uuid) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.courses.detail(uuid) });
    },
  });
}

export function useSubmitCourseForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioCoursesClient.submitForReview(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_COURSES_PREFIX });
    },
  });
}

export function useWithdrawCourseSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioCoursesClient.withdrawSubmission(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_COURSES_PREFIX });
    },
  });
}

export function useUnpublishCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => studioCoursesClient.unpublish(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STUDIO_COURSES_PREFIX });
    },
  });
}

export function useAttachVideoToCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseUuid, videoUuid }: { courseUuid: string; videoUuid: string }) =>
      studioCoursesClient.attachVideo(courseUuid, videoUuid),
    onSuccess: (_data, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.courses.detail(courseUuid) });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

export function useDetachVideoFromCourse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseUuid, videoUuid }: { courseUuid: string; videoUuid: string }) =>
      studioCoursesClient.detachVideo(courseUuid, videoUuid),
    onSuccess: (_data, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.courses.detail(courseUuid) });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

/**
 * Batch attach N videos to a course or series in one shot (v3.1 §3).
 * Always returns 200 on partial success — caller inspects the 4 buckets
 * (`attached`, `conflicts`, `unauthorized_uuids`, `not_found_uuids`).
 */
export function useBatchAttachVideos() {
  const queryClient = useQueryClient();
  return useMutation<BatchAttachVideosResponse, Error, BatchAttachVideosPayload>({
    mutationFn: (payload) => studioCoursesClient.batchAttachVideos(payload),
    onSuccess: (_data, payload) => {
      const detailKey =
        payload.target_type === 'course'
          ? queryKeys.studio.courses.detail(payload.target_uuid)
          : queryKeys.studio.series.detail(payload.target_uuid);
      queryClient.invalidateQueries({ queryKey: detailKey });
      queryClient.invalidateQueries({ queryKey: STUDIO_VIDEOS_PREFIX });
    },
  });
}

export function useReorderCourseVideos() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courseUuid, payload }: { courseUuid: string; payload: ReorderVideosPayload }) =>
      studioCoursesClient.reorderVideos(courseUuid, payload),
    onMutate: async ({ courseUuid, payload }) => {
      const detailKey = queryKeys.studio.courses.detail(courseUuid);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData(detailKey);
      queryClient.setQueryData(detailKey, (current: unknown) => {
        if (!current || typeof current !== 'object' || !('videos' in current)) return current;
        const detail = current as { videos: { uuid: string; display_order: number }[] };
        const orderMap = new Map(payload.order.map((o) => [o.uuid, o.display_order]));
        const nextVideos = detail.videos
          .map((v) => ({ ...v, display_order: orderMap.get(v.uuid) ?? v.display_order }))
          .sort((a, b) => a.display_order - b.display_order);
        return { ...detail, videos: nextVideos };
      });
      return { previous, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.detailKey, context.previous);
      }
    },
    onSettled: (_data: ReorderVideosResponse | undefined, _err, { courseUuid }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.studio.courses.detail(courseUuid) });
    },
  });
}
